import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { getSavingsMonths, getSavingsForMonth, getSavingsTrends } from '../services/savings';

const ACCOUNT_TYPE_LABELS = {
  retirement: 'Retirement',
  savings: 'Savings',
  investment: 'Investment',
  education: 'Education',
  health_savings: 'Health Savings',
  checking: 'Checking',
  other: 'Other',
};

const ACCOUNT_TYPE_COLORS = {
  retirement: '#1976d2',
  savings: '#2e7d32',
  investment: '#ed6c02',
  education: '#9c27b0',
  health_savings: '#0288d1',
  checking: '#558b2f',
  other: '#757575',
};

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function formatCurrencyFull(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(val);
}

function formatMonth(m) {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const names = [
    '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  return `${names[parseInt(mo, 10)]} ${y}`;
}

function formatMonthFull(m) {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const names = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${names[parseInt(mo, 10)]} ${y}`;
}

export default function Savings() {
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [savingsData, setSavingsData] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMonths = useCallback(async () => {
    try {
      const res = await getSavingsMonths();
      const m = res.data.months || [];
      setMonths(m);
      if (m.length > 0 && !selectedMonth) {
        setSelectedMonth(m[0]);
      }
    } catch {
      // No savings data yet
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  const loadData = useCallback(async () => {
    if (!selectedMonth) return;
    setLoading(true);
    try {
      const [savingsRes, trendsRes] = await Promise.all([
        getSavingsForMonth(selectedMonth),
        getSavingsTrends(),
      ]);
      setSavingsData(savingsRes.data);
      setTrends(trendsRes.data.trends || []);
    } catch {
      setSavingsData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    loadMonths();
  }, [loadMonths]);

  useEffect(() => {
    if (selectedMonth) {
      loadData();
    }
  }, [selectedMonth, loadData]);

  if (loading && months.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (months.length === 0) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>Savings</Typography>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No savings data yet. Upload Excel files on the Upload page to track savings here.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Savings</Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Month</InputLabel>
          <Select
            value={selectedMonth}
            label="Month"
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {months.map((m) => (
              <MenuItem key={m} value={m}>{formatMonthFull(m)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          {savingsData && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <AccountBalanceIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h5" fontWeight={700}>
                      {formatCurrency(savingsData.total_assets)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Assets
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <AccountBalanceIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                    <Typography variant="h5" fontWeight={700}>
                      {formatCurrency(savingsData.total_liquid)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Liquid Assets
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    {(() => {
                      const currentTrend = trends.find((t) => t.month === selectedMonth);
                      const growth = currentTrend?.growth;
                      const pct = currentTrend?.growth_pct;
                      const isPositive = growth != null && growth >= 0;
                      return (
                        <>
                          {isPositive
                            ? <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                            : <TrendingDownIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                          }
                          <Typography variant="h5" fontWeight={700} color={isPositive ? 'success.main' : 'error.main'}>
                            {growth != null ? `${isPositive ? '+' : ''}${formatCurrency(growth)}` : '—'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Month-over-Month {pct != null ? `(${pct > 0 ? '+' : ''}${pct}%)` : ''}
                          </Typography>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Trends Chart */}
          {trends.length > 1 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Total Assets Over Time</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tickFormatter={formatMonth} />
                    <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(value) => [formatCurrencyFull(value), 'Total Assets']}
                      labelFormatter={formatMonthFull}
                    />
                    <Line
                      type="monotone"
                      dataKey="total_assets"
                      stroke="#1976d2"
                      strokeWidth={3}
                      dot={{ fill: '#1976d2', r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Account Groups */}
          {savingsData && savingsData.groups && savingsData.groups.map((group) => (
            <Card key={group.account_type} sx={{ mb: 2 }}>
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Chip
                    label={ACCOUNT_TYPE_LABELS[group.account_type] || group.account_type}
                    sx={{
                      backgroundColor: ACCOUNT_TYPE_COLORS[group.account_type] || '#757575',
                      color: '#fff',
                      fontWeight: 600,
                    }}
                  />
                  <Typography variant="h6" fontWeight={700}>
                    {formatCurrencyFull(group.total)}
                  </Typography>
                </Box>
              </CardContent>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Balance</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, width: 80 }}>Liquid</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.accounts.map((acct) => (
                      <TableRow key={acct.id}>
                        <TableCell>{acct.account_name}</TableCell>
                        <TableCell align="right">{formatCurrencyFull(acct.balance)}</TableCell>
                        <TableCell align="center">
                          {acct.is_liquid && (
                            <Chip size="small" label="Yes" color="success" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          ))}
        </>
      )}
    </Box>
  );
}
